import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt as ReceiptIcon, Search, Plus } from 'lucide-react';
import { apiGet } from './appApi';
import { formatDateShort } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';

export const AppReceipts = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    apiGet('/api/admin/receipts')
      .then(setList)
      .catch((err) => logger.error('Load receipts:', err))
      .finally(() => setLoading(false));
  }, []);

  const q = query.toLowerCase();
  const filtered = list.filter(r =>
    !q ||
    (r.client_name || '').toLowerCase().includes(q) ||
    (r.receipt_number || '').toLowerCase().includes(q)
  );

  return (
    <div>
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div
          className="px-4 pb-3"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 16px)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold text-gray-900">Receipts</h1>
            <button
              onClick={() => navigate('/admin/receipts/new')}
              className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center active:scale-95"
              aria-label="New receipt"
              data-testid="new-receipt-btn"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Search receipts"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-100 rounded-xl border-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
      </div>

      <div className="px-4 py-3 space-y-2">
        {loading ? (
          [0, 1, 2].map(i => <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-xl" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <ReceiptIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {query ? 'No matches' : 'No receipts yet'}
            </p>
          </div>
        ) : (
          filtered.map(r => (
            <button
              key={r.id}
              onClick={() => navigate(`/admin/receipts/${r.id}`)}
              className="w-full bg-white border border-gray-200 rounded-xl p-3 text-left active:bg-gray-50"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="font-semibold text-gray-900 truncate">{r.client_name}</p>
                <p className="text-sm font-bold text-emerald-600">
                  {r.currency}{(r.total || 0).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 font-mono">{r.receipt_number}</p>
                <p className="text-xs text-gray-400">
                  {r.receipt_date && formatDateShort(r.receipt_date)}
                </p>
              </div>
              {r.invoice_id ? (
                <p className="text-[11px] text-gray-400 mt-1">ref: {r.invoice_number}</p>
              ) : (
                <p className="text-[11px] text-gray-400 mt-1 italic">manual receipt</p>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
};
